import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Source } from "../../types/cuadra";
import { useMessageFeedback } from "../../contexts/MessageFeedbackContext";
import { useStrings } from "./StringsContext";
import { sanitizeFeedbackComment } from "../../utils/sanitize";

type TargetKind = "response" | "sources";

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (rating: "positive" | "negative") => void;
  rating: "positive" | "negative";
  chatId: string;
  messageId: string | null;
  sources: Source[] | null;
}

function deduplicateSourcesByFilename(sources: Source[]): Source[] {
  const byFilename = new Map<string, Source>();
  for (const source of sources) {
    if (!source.chunkId) continue;
    const existing = byFilename.get(source.filename);
    if (!existing || source.score > existing.score) {
      byFilename.set(source.filename, source);
    }
  }
  return Array.from(byFilename.values()).sort((a, b) => b.score - a.score);
}

export function FeedbackModal({
  isOpen,
  onClose,
  onSubmitted,
  rating,
  chatId,
  messageId,
  sources,
}: FeedbackModalProps) {
  const feedbackCtx = useMessageFeedback();
  const str = useStrings();
  const dialogRef = useRef<HTMLDivElement>(null);

  const feedbackableSources = sources
    ? deduplicateSourcesByFilename(sources)
    : [];
  const hasSources = feedbackableSources.length > 0;

  const [targetKind, setTargetKind] = useState<TargetKind>("response");
  const [selectedChips, setSelectedChips] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTargetKind("response");
      setSelectedChips(new Set());
      setComment("");
      setSubmitted(false);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen || !feedbackCtx) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const toggleChip = (chip: string) => {
    setSelectedChips((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) {
        next.delete(chip);
      } else {
        next.add(chip);
      }
      return next;
    });
  };

  const getChips = (): string[] => {
    if (rating === "positive" && targetKind === "response")
      return str.feedbackChipsPositiveResponse;
    if (rating === "negative" && targetKind === "response")
      return str.feedbackChipsNegativeResponse;
    if (rating === "positive" && targetKind === "sources")
      return str.feedbackChipsPositiveSources;
    return str.feedbackChipsNegativeSources;
  };

  const chips = getChips();

  const handleSubmit = () => {
    const sanitized = sanitizeFeedbackComment(comment);
    const chipLabels = Array.from(selectedChips);
    const parts = [...chipLabels];
    if (sanitized) parts.push(sanitized);
    const fullComment = parts.join("\n");

    if (targetKind === "sources") {
      for (const source of feedbackableSources) {
        feedbackCtx.submitSourceFeedback(
          chatId,
          messageId,
          source.chunkId!,
          rating,
        );
      }
    }

    if (targetKind === "response") {
      feedbackCtx.submitComment(chatId, messageId, fullComment, rating);
    }

    onSubmitted?.(rating);
    setSubmitted(true);
    setTimeout(onClose, 600);
  };

  const title =
    rating === "positive"
      ? str.feedbackModalTitlePositive
      : str.feedbackModalTitleNegative;

  const placeholder =
    rating === "positive"
      ? str.feedbackModalPlaceholderPositive
      : str.feedbackModalPlaceholderNegative;

  const hasSelection = selectedChips.size > 0 || comment.trim().length > 0;

  const toggleBtnBase =
    "cuadra-px-3 cuadra-py-1.5 cuadra-text-xs cuadra-rounded-lg cuadra-transition-colors cuadra-cursor-pointer cuadra-font-medium";
  const toggleBtnActive =
    "cuadra-bg-foreground cuadra-text-background";
  const toggleBtnInactive =
    "cuadra-text-muted-foreground hover:cuadra-text-foreground";

  return createPortal(
    <div
      className="cuadra-fixed cuadra-inset-0 cuadra-z-50 cuadra-flex cuadra-items-center cuadra-justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="cuadra-relative cuadra-w-full cuadra-max-w-md cuadra-mx-4 cuadra-rounded-lg cuadra-shadow-lg cuadra-animate-in cuadra-fade-in cuadra-duration-200"
        style={{
          backgroundColor: "var(--cuadra-background)",
          border: "1px solid var(--cuadra-border-subtle)",
        }}
      >
        {/* Header */}
        <div
          className="cuadra-px-5 cuadra-pt-4 cuadra-pb-3"
          style={{ borderBottom: "1px solid var(--cuadra-border-subtle)" }}
        >
          <h2 className="cuadra-text-sm cuadra-font-semibold cuadra-text-foreground">
            {title}
          </h2>
        </div>

        {submitted ? (
          <div className="cuadra-px-5 cuadra-py-8 cuadra-text-center">
            <p className="cuadra-text-sm cuadra-text-muted-foreground cuadra-font-brand">
              Thanks for your feedback!
            </p>
          </div>
        ) : (
          <>
            {/* Body */}
            <div className="cuadra-px-5 cuadra-py-3 cuadra-flex cuadra-flex-col cuadra-gap-2.5">
              {/* Toggle buttons — only when feedbackable sources exist */}
              {hasSources && (
                <div className="cuadra-flex cuadra-flex-col cuadra-gap-2">
                  <span className="cuadra-text-xs cuadra-font-medium cuadra-text-muted-foreground">
                    What is this feedback about?
                  </span>
                  <div
                    className="cuadra-inline-flex cuadra-self-start cuadra-rounded-lg cuadra-p-0.5"
                    style={{ backgroundColor: "var(--cuadra-muted)" }}
                    role="group"
                    aria-label="Feedback target"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setTargetKind("response");
                        setSelectedChips(new Set());
                      }}
                      className={`${toggleBtnBase} ${targetKind === "response" ? toggleBtnActive : toggleBtnInactive}`}
                      aria-pressed={targetKind === "response"}
                    >
                      {str.feedbackTargetResponse}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTargetKind("sources");
                        setSelectedChips(new Set());
                      }}
                      className={`${toggleBtnBase} ${targetKind === "sources" ? toggleBtnActive : toggleBtnInactive}`}
                      aria-pressed={targetKind === "sources"}
                    >
                      {str.feedbackTargetSources}
                    </button>
                  </div>
                </div>
              )}

              {/* Chips */}
              <div className="cuadra-flex cuadra-flex-wrap cuadra-gap-1.5">
                {chips.map((chip) => {
                  const isSelected = selectedChips.has(chip);
                  return (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => toggleChip(chip)}
                      className={`cuadra-px-2.5 cuadra-py-1 cuadra-text-xs cuadra-rounded-full cuadra-border cuadra-transition-colors cuadra-cursor-pointer ${
                        isSelected
                          ? "cuadra-border-foreground cuadra-bg-foreground cuadra-text-background"
                          : "cuadra-border-border cuadra-text-muted-foreground hover:cuadra-bg-muted/60 hover:cuadra-text-foreground"
                      }`}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>

              {/* Textarea */}
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={placeholder}
                maxLength={2000}
                rows={3}
                className="cuadra-w-full cuadra-px-3 cuadra-py-2 cuadra-text-sm cuadra-rounded-lg cuadra-border cuadra-border-border cuadra-bg-transparent cuadra-text-foreground placeholder:cuadra-text-muted-foreground focus:cuadra-outline-none focus:cuadra-ring-1 focus:cuadra-ring-ring cuadra-resize-none cuadra-font-brand"
              />
            </div>

            {/* Footer */}
            <div
              className="cuadra-flex cuadra-justify-end cuadra-gap-2 cuadra-px-5 cuadra-py-3"
              style={{ borderTop: "1px solid var(--cuadra-border-subtle)" }}
            >
              <button
                type="button"
                onClick={onClose}
                className="cuadra-h-9 cuadra-px-4 cuadra-py-2 cuadra-text-sm cuadra-font-medium cuadra-rounded-md cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-cursor-pointer"
              >
                {str.feedbackCancel}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!hasSelection}
                className="cuadra-h-9 cuadra-px-4 cuadra-py-2 cuadra-text-sm cuadra-font-medium cuadra-rounded-md cuadra-text-primary-foreground cuadra-shadow hover:cuadra-opacity-90 cuadra-transition-colors disabled:cuadra-opacity-50 disabled:cuadra-cursor-not-allowed cuadra-cursor-pointer"
                style={{ backgroundColor: "var(--cuadra-primary)" }}
              >
                {str.feedbackSubmit}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
